import { SubmissionErrors } from 'final-form';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Col, Row } from 'antd';
import { RouteComponentProps } from 'react-router-dom';

import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { FieldType } from '~src/types';
import { validatorMaxLength80, validatorRequired } from '~src/services/validators';
import CardsRadio from '~src/shared/form/cardsRadio/CardsRadio';
import { SurveyTypeData, surveyTypeNames, surveyTypes } from '~src/modules/surveys/constants/surveyTypes';
import { StoreI } from '~src/rootReducer';
import { getCompanyContentLocale } from '~src/services/session';
import { SurveySubjectTypes, SurveyTypes } from '~src/interfaces/surveys.interfaces';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import { ButtonWrapper } from '~src/modules/attributes/components/add/attributeAddForm.sc';
import { getDefaultSurveyInvitation, NewSurveySubmitData } from '~src/modules/surveys/interfaces';
import { routes } from '~src/routingConfig';

import { AttrLabel, AttrTypesContainer, CreateSurveyBtn, StyledForm } from './surveyAddForm.sc';

interface OwnProps extends Pick<RouteComponentProps<any>, 'history'> {
  submitFrm(survey: NewSurveySubmitData): Promise<SubmissionErrors>;
}

interface StateProps {
  loadingAdd: boolean;
  defaultContentLocale: string;
}

type Props = OwnProps & StateProps & WithTranslation;

interface State {
  surveyTypes: SurveyTypeData[];
}

interface FormValues {
  name: string;
  subject: keyof SurveyTypes | SurveySubjectTypes;
}

class SurveyAddForm extends React.Component<Props, State> {
  state = {
    surveyTypes,
  };

  goBack = () => {
    this.props.history.push(routes.contentSurveys.surveys);
  };

  mapValuesBeforeSubmit = (values: FormValues) => {
    const submitValues: NewSurveySubmitData = {
      name: { [this.props.defaultContentLocale]: values.name.trim() },
      subject: SurveySubjectTypes.General,
      invitation: getDefaultSurveyInvitation(this.props.t),
    };

    switch (values.subject) {
      case surveyTypeNames.three_sixty:
      case surveyTypeNames.bottom_up:
      case surveyTypeNames.top_down:
        submitValues.subject = SurveySubjectTypes.People;
        submitValues.useCase = values.subject;
        break;
      default:
        submitValues.subject = values.subject;
    }

    return this.props.submitFrm(submitValues);
  };

  renderField = ({ autoFocus, input, label, meta: { touched, error, submitError }, labelKey }: FieldType) => (
    <CustomFormInput
      {...input}
      autoFocus={autoFocus}
      label={label}
      type={input.type}
      error={touched ? error || submitError : null}
      labelKey={labelKey}
    />
  );

  render() {
    const { t, loadingAdd } = this.props;
    const surveyNamePlaceholder = t('x.surveys.add.input.placeholder.survey_name');

    return (
      <Form /*todo touchOnBlur=false*/
        initialValues={{ subject: SurveySubjectTypes.General }}
        onSubmit={this.mapValuesBeforeSubmit}
      >
        {({ handleSubmit, values }) => (
          <StyledForm onSubmit={handleSubmit} noValidate>
            <AttrTypesContainer>
              {this.state.surveyTypes.map((item, index) => (
                <AttrLabel htmlFor={`subject_${index}`} key={item.value} $itemsLength={surveyTypes.length}>
                  <CardsRadio
                    t={t}
                    itemTypeSelected={values.subject}
                    item={item}
                    index={index}
                    fieldName="subject"
                  />
                </AttrLabel>
              ))}
            </AttrTypesContainer>
            <Row>
              <Col lg={24} style={{ marginTop: '50px' }}>
                <Field
                  name="name"
                  label={surveyNamePlaceholder}
                  type="text"
                  children={this.renderField}
                  validate={composeValidators([validatorRequired, validatorMaxLength80])}
                  labelKey="x.surveys.add.input.placeholder.survey_name"
                />
              </Col>
            </Row>
            <ButtonWrapper>
              <SecondaryButton
                type="button"
                style={{ whiteSpace: 'nowrap', minWidth: '145px' }}
                onClick={this.goBack}
              >
                <span data-translation="x.surveys.add.button_cancel">{t('x.surveys.add.button_cancel')}</span>
              </SecondaryButton>
              <CreateSurveyBtn isLoading={loadingAdd}>
                <span data-translation="x.surveys.add.button.create">{t('x.surveys.add.button.create')}</span>
              </CreateSurveyBtn>
            </ButtonWrapper>
          </StyledForm>
        )}
      </Form>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => {
  return {
    defaultContentLocale: getCompanyContentLocale(state),
    loadingAdd: state.surveyManagement.surveys.loadingAdd,
  };
};

const SurveyAddFormT = withTranslation()(SurveyAddForm);
const SurveyAddFormS = connect(mapStateToProps)(SurveyAddFormT);

export default SurveyAddFormS;
